import moment from 'moment';
import {
  formatHotelDate,
  formatHotelDateTimeParts,
  getHotelTimezone,
  toHotelPickerMoment,
} from './dateUtils';

/**
 * Stay dates (check_in_date / check_out_date) are the booked period shown on cards and calendars.
 * check_in_time / check_out_time are UTC ISO — always render via hotel timezone + hotel date format.
 * check_in_time_display / check_out_time_display are legacy backend strings used only as fallback.
 */
function parseBackendTimeDisplay(display) {
  if (!display || !String(display).trim()) return null;
  const parts = String(display).split(', ');
  const date = parts[0]?.trim() || null;
  const time = parts[1]?.trim() || null;
  if (!date) return null;
  return { date, time, full: time ? `${date}, ${time}` : date };
}

function splitBackendTimeDisplay(display) {
  return parseBackendTimeDisplay(display)?.time || null;
}

function resolveHotelTimezone(booking) {
  const fromBooking = booking?.hotel_timezone && String(booking.hotel_timezone).trim();
  return fromBooking || getHotelTimezone();
}

/** Same calendar check-in and check-out (OTA day-use / hourly-style stay). */
export function isSameDayStay(booking) {
  const checkInRaw = booking?.check_in_date || booking?.start_date || booking?.start;
  const checkOutRaw = booking?.check_out_date || booking?.end_date || booking?.end;
  if (!checkInRaw || !checkOutRaw) return false;
  const checkIn = moment(checkInRaw);
  const checkOut = moment(checkOutRaw);
  if (!checkIn.isValid() || !checkOut.isValid()) return false;
  return checkIn.isSame(checkOut, 'day');
}

/**
 * Exclusive end date for room-night rows: nights cover [check_in, end).
 * Mirrors backend booking_stay_exclusive_end — a same-day stay (OTA day-use / hourly)
 * is stored as one night on the check-in date.
 */
export function resolveStayExclusiveEnd(checkInDate, checkOutDate) {
  const checkIn = checkInDate ? moment(checkInDate).startOf('day') : null;
  const checkOut = checkOutDate ? moment(checkOutDate).startOf('day') : null;
  if (!checkIn?.isValid() || !checkOut?.isValid()) return null;
  return checkOut.isAfter(checkIn, 'day') ? checkOut : checkIn.clone().add(1, 'day');
}

/**
 * Format a minute duration for chips / summaries.
 * Under 24h: "11h 30m" / "11 hours 30 minutes"
 * 24h+: "8d 8h 46m" (no total-hours decimal)
 */
export function formatStayDurationParts(totalMinutes) {
  const mins = Math.max(0, Math.round(Number(totalMinutes) || 0));
  const totalHours = Math.floor(mins / 60);
  const minutes = mins % 60;
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;

  const dayWord = days === 1 ? 'day' : 'days';
  const hourWord = hours === 1 ? 'hour' : 'hours';
  const minuteWord = minutes === 1 ? 'minute' : 'minutes';
  const totalHourWord = totalHours === 1 ? 'hour' : 'hours';

  if (totalHours === 0) {
    return {
      chipLabel: `${minutes}m`,
      summaryText: `${minutes} ${minuteWord}`,
      days: 0,
      hours: 0,
      minutes,
      totalHours,
      totalMinutes: mins,
    };
  }

  if (days >= 1) {
    const compactParts = [`${days}d`];
    if (hours > 0) compactParts.push(`${hours}h`);
    if (minutes > 0) compactParts.push(`${minutes}m`);
    const chipLabel = compactParts.join(' ');

    const longParts = [`${days} ${dayWord}`];
    if (hours > 0) longParts.push(`${hours} ${hourWord}`);
    if (minutes > 0) longParts.push(`${minutes} ${minuteWord}`);

    return {
      chipLabel,
      summaryText: longParts.join(' '),
      days,
      hours,
      minutes,
      totalHours,
      totalMinutes: mins,
    };
  }

  if (minutes === 0) {
    return {
      chipLabel: `${totalHours}h`,
      summaryText: `${totalHours} ${totalHourWord}`,
      days: 0,
      hours: totalHours,
      minutes: 0,
      totalHours,
      totalMinutes: mins,
    };
  }

  return {
    chipLabel: `${totalHours}h ${minutes}m`,
    summaryText: `${totalHours} ${totalHourWord} ${minutes} ${minuteWord}`,
    days: 0,
    hours: totalHours,
    minutes,
    totalHours,
    totalMinutes: mins,
  };
}

/** Actual stay length from check_in_time → check_out_time (UTC ISO). */
export function resolveActualStayDurationMinutes(booking) {
  const rawIn = booking?.check_in_time || booking?.actual_checkin_time;
  const rawOut = booking?.check_out_time || booking?.actual_checkout_time;
  if (!rawIn || !rawOut) return null;
  const start = moment.utc(rawIn);
  const end = moment.utc(rawOut);
  if (!start.isValid() || !end.isValid() || !end.isAfter(start)) return null;
  return end.diff(start, 'minutes', true);
}

function roomItemDateKeys(roomItems) {
  const keys = new Set();
  (Array.isArray(roomItems) ? roomItems : []).forEach((item) => {
    if (!item?.date) return;
    const key = moment.isMoment(item.date)
      ? item.date.format('YYYY-MM-DD')
      : String(item.date).slice(0, 10);
    if (key) keys.add(key);
  });
  return [...keys].sort();
}

/** Earliest segment_started_at / latest segment_ended_at from room items. */
export function collectRoomSegmentTimestamps(roomItems) {
  const tz = null; // resolved per booking by callers when converting
  let startedAt = null;
  let endedAt = null;
  (Array.isArray(roomItems) ? roomItems : []).forEach((item) => {
    const rawStart = item?.segment_started_at || item?.room_changed_at;
    const rawEnd = item?.segment_ended_at;
    if (rawStart) {
      const m = moment.utc(rawStart);
      if (m.isValid() && (!startedAt || m.isBefore(startedAt))) startedAt = m;
    }
    if (rawEnd) {
      const m = moment.utc(rawEnd);
      if (m.isValid() && (!endedAt || m.isAfter(endedAt))) endedAt = m;
    }
  });
  return {
    segmentStartedAt: startedAt ? startedAt.toISOString() : null,
    segmentEndedAt: endedAt ? endedAt.toISOString() : null,
    _tz: tz,
  };
}

/**
 * Per-room (or per room-change segment) stay meta: booked nights + clock duration.
 * Prefers BookingItem.segment_started_at / segment_ended_at (actual room-move times).
 * Falls back to booking actual CI/CO on arrival/departing rooms, else midnight bounds.
 */
export function resolveRoomSegmentStayMeta(booking, roomItems, options = {}) {
  const dates = roomItemDateKeys(roomItems);
  const nights = dates.length;
  if (!nights) return null;

  const first = dates[0];
  const last = dates[dates.length - 1];
  const segmentEndDay = moment(last).add(1, 'day').format('YYYY-MM-DD');
  const isArrivalSegment = options.isArrivalSegment === true;
  const isDepartingSegment = options.isDepartingSegment === true;
  const tz = resolveHotelTimezone(booking);

  const { segmentStartedAt, segmentEndedAt } = collectRoomSegmentTimestamps(roomItems);
  const segmentStart = segmentStartedAt ? toHotelPickerMoment(segmentStartedAt, tz) : null;
  const segmentEnd = segmentEndedAt ? toHotelPickerMoment(segmentEndedAt, tz) : null;
  const actualIn = resolveActualCheckInMoment(booking);
  const actualOut = resolveActualCheckOutMoment(booking);

  let start = moment(first).startOf('day');
  let end = moment(segmentEndDay).startOf('day');

  if (segmentStart?.isValid()) {
    start = segmentStart.clone();
  } else if (actualIn?.isValid() && (isArrivalSegment || actualIn.isSame(first, 'day'))) {
    start = actualIn.clone();
  }

  if (segmentEnd?.isValid()) {
    end = segmentEnd.clone();
  } else if (actualOut?.isValid() && (isDepartingSegment || actualOut.isSame(segmentEndDay, 'day'))) {
    end = actualOut.clone();
  }

  if (!end.isAfter(start)) {
    end = start.clone().add(Math.max(nights, 1), 'day');
  }

  const parts = formatStayDurationParts(end.diff(start, 'minutes', true));
  return {
    nights,
    bookedChipLabel: `${nights} N`,
    stayChipLabel: parts.chipLabel,
    summaryText: `${nights} night${nights !== 1 ? 's' : ''} · ${parts.summaryText}`,
    totalMinutes: parts.totalMinutes,
    firstDate: first,
    lastDate: last,
    segmentEndDay,
    segmentStartedAt,
    segmentEndedAt,
  };
}

/** Booked night count (scheduled stay). Prefer API `nights`, else date diff. */
export function resolveBookedNights(booking) {
  if (booking?.nights != null && Number.isFinite(Number(booking.nights))) {
    return Math.max(0, Number(booking.nights));
  }
  const checkInRaw = booking?.check_in_date;
  const checkOutRaw = booking?.scheduled_check_out_date || booking?.check_out_date;
  if (!checkInRaw || !checkOutRaw) return 0;
  const checkIn = moment(checkInRaw);
  const checkOut = moment(checkOutRaw);
  if (!checkIn.isValid() || !checkOut.isValid()) return 0;
  const diff = checkOut.diff(checkIn, 'days');
  // Same calendar day (OTA day-use) counts as 1 night in PMS pricing.
  if (diff === 0) return 1;
  return Math.max(0, diff);
}

/**
 * Labels for nights vs hourly vs checked-out actual stay (drawers / detail pages).
 * Checked-out: separate chips — "Booked 16 N" and "8d 8h 46m".
 */
export function getStayDurationMeta(booking) {
  const status = String(booking?.status || '').toLowerCase().replace(/\s+/g, '_');
  const nights = resolveBookedNights(booking);
  const nightLabel = `${nights} night${nights !== 1 ? 's' : ''}`;

  if (status === 'checked_out') {
    const stayMinutes = resolveActualStayDurationMinutes(booking);
    if (stayMinutes != null) {
      const parts = formatStayDurationParts(stayMinutes);
      const bookedChipLabel = nights > 0 ? `Booked ${nights} N` : null;
      const stayChipLabel = parts.chipLabel;
      return {
        isHourly: false,
        isActualStay: true,
        fieldLabel: 'Stay:',
        bookedChipLabel,
        stayChipLabel,
        chipLabel: stayChipLabel,
        summaryText: bookedChipLabel
          ? `${bookedChipLabel} · ${parts.summaryText}`
          : parts.summaryText,
        nights: nights || 0,
        totalMinutes: parts.totalMinutes,
      };
    }
  }

  if (isSameDayStay(booking)) {
    return {
      isHourly: true,
      isActualStay: false,
      fieldLabel: 'Stay type:',
      chipLabel: 'Hourly',
      bookedChipLabel: null,
      stayChipLabel: null,
      summaryText: 'Hourly booking',
      nights: 0,
    };
  }

  return {
    isHourly: false,
    isActualStay: false,
    fieldLabel: 'Total Nights:',
    chipLabel: String(nights || 0),
    bookedChipLabel: null,
    stayChipLabel: null,
    summaryText: nightLabel,
    nights: nights || 0,
  };
}

export function resolveStayCheckInDisplay(booking) {
  const date = booking?.check_in_date ? formatHotelDate(booking.check_in_date) : null;
  const tz = resolveHotelTimezone(booking);
  const raw = booking?.check_in_time || booking?.actual_checkin_time;
  const fromRaw = formatHotelDateTimeParts(raw, tz);
  if (fromRaw?.time) {
    return { date, time: fromRaw.time };
  }
  const timeFromDisplay = splitBackendTimeDisplay(booking?.check_in_time_display);
  return {
    date,
    time: timeFromDisplay || null,
  };
}

export function resolveStayCheckOutDisplay(booking) {
  const date = booking?.check_out_date ? formatHotelDate(booking.check_out_date) : null;
  const tz = resolveHotelTimezone(booking);
  const raw = booking?.check_out_time || booking?.actual_checkout_time;
  const fromRaw = formatHotelDateTimeParts(raw, tz);
  if (fromRaw?.time) {
    return { date, time: fromRaw.time };
  }
  const timeFromDisplay = splitBackendTimeDisplay(booking?.check_out_time_display);
  return {
    date,
    time: timeFromDisplay || null,
  };
}

/** Actual check-in/out timestamp (full date + time) for chips below stay date fields. */
export function resolveActualCheckInDisplay(booking) {
  const tz = resolveHotelTimezone(booking);
  const raw = booking?.check_in_time || booking?.actual_checkin_time;
  const fromRaw = formatHotelDateTimeParts(raw, tz);
  if (fromRaw) return fromRaw;
  return parseBackendTimeDisplay(booking?.check_in_time_display);
}

export function resolveActualCheckOutDisplay(booking) {
  const tz = resolveHotelTimezone(booking);
  const raw = booking?.check_out_time || booking?.actual_checkout_time;
  const fromRaw = formatHotelDateTimeParts(raw, tz);
  if (fromRaw) return fromRaw;
  return parseBackendTimeDisplay(booking?.check_out_time_display);
}

export function actualCheckInDiffersFromStay(booking) {
  const actual = resolveActualCheckInDisplay(booking);
  const stay = resolveStayCheckInDisplay(booking);
  if (!actual?.date) return false;
  if (!stay?.date) return true;
  return actual.date !== stay.date;
}

export function actualCheckOutDiffersFromStay(booking) {
  const actual = resolveActualCheckOutDisplay(booking);
  const stay = resolveStayCheckOutDisplay(booking);
  if (!actual?.date) return false;
  if (!stay?.date) return true;
  return actual.date !== stay.date;
}

/** Moment value for datetime pickers (hotel wall-clock via naive local moment). */
export function resolveActualCheckInMoment(booking) {
  const raw = booking?.check_in_time || booking?.actual_checkin_time;
  if (!raw) return null;
  return toHotelPickerMoment(raw, resolveHotelTimezone(booking));
}

export function resolveActualCheckOutMoment(booking) {
  const raw = booking?.check_out_time || booking?.actual_checkout_time;
  if (!raw) return null;
  return toHotelPickerMoment(raw, resolveHotelTimezone(booking));
}

/** Parse "HH:MM" / "HH:MM:SS" into { hours, minutes }. */
function parseHotelPolicyTime(raw, defaultHours, defaultMinutes = 0) {
  if (raw == null || raw === '') {
    return { hours: defaultHours, minutes: defaultMinutes };
  }
  if (typeof raw === 'object' && raw !== null) {
    const h = Number(raw.hours ?? raw.hour);
    const m = Number(raw.minutes ?? raw.minute);
    if (Number.isFinite(h)) {
      return {
        hours: Math.min(23, Math.max(0, h)),
        minutes: Number.isFinite(m) ? Math.min(59, Math.max(0, m)) : defaultMinutes,
      };
    }
  }
  const parts = String(raw).trim().split(':');
  const hours = Number.parseInt(parts[0], 10);
  const minutes = Number.parseInt(parts[1], 10);
  return {
    hours: Number.isFinite(hours) ? Math.min(23, Math.max(0, hours)) : defaultHours,
    minutes: Number.isFinite(minutes) ? Math.min(59, Math.max(0, minutes)) : defaultMinutes,
  };
}

/**
 * Expected check-out wall-clock on the scheduled checkout date (hotel policy time).
 * Used for in-house / not-yet-checked-out bars — not actual checkout.
 */
export function resolveExpectedCheckOutMoment(booking) {
  const dateRaw = booking?.scheduled_check_out_date || booking?.check_out_date;
  if (!dateRaw) return null;
  const day = moment(dateRaw);
  if (!day.isValid()) return null;
  const { hours, minutes } = parseHotelPolicyTime(
    booking?.hotel_check_out_time || booking?.expected_check_out_time,
    11,
    0,
  );
  return moment({
    year: day.year(),
    month: day.month(),
    date: day.date(),
    hour: hours,
    minute: minutes,
    second: 0,
    millisecond: 0,
  });
}

/** Expected check-in wall-clock on the scheduled check-in date (hotel policy time). */
export function resolveExpectedCheckInMoment(booking) {
  const dateRaw = booking?.check_in_date || booking?.start_date || booking?.start;
  if (!dateRaw) return null;
  const day = moment(dateRaw);
  if (!day.isValid()) return null;
  const { hours, minutes } = parseHotelPolicyTime(
    booking?.hotel_check_in_time || booking?.expected_check_in_time,
    14,
    0,
  );
  return moment({
    year: day.year(),
    month: day.month(),
    date: day.date(),
    hour: hours,
    minute: minutes,
    second: 0,
    millisecond: 0,
  });
}

/** Fraction of a calendar day (0–1) from a hotel wall-clock moment. */
export function dayFractionFromMoment(m) {
  if (!m || !moment.isMoment(m) || !m.isValid()) return 0.5;
  const minutes = m.hours() * 60 + m.minutes() + m.seconds() / 60;
  return Math.min(0.98, Math.max(0.02, minutes / (24 * 60)));
}

/**
 * Room board bars: align start/end to clock times (hotel TZ) for positioning.
 *
 * Rules:
 * - Checked out: actual check-in → actual check-out (existing behavior).
 * - Not checked out: actual check-in (when present) → expected checkout
 *   (scheduled date + hotel check-out policy time). Pre-arrival uses expected CI.
 * - Never expand a room-change segment outside its own start/end dates.
 * - Never pull check-in backward to booking actual CI (mid-stay room B when A is off-screen).
 *
 * @param {object} [options]
 * @param {boolean} [options.isDepartingRoom]
 * @param {boolean} [options.isArrivalRoom]
 * @param {string|object} [options.segmentStartedAt]
 * @param {string|object} [options.segmentEndedAt]
 * @param {string|object} [options.segmentStartDate] — room segment first night (inclusive)
 * @param {string|object} [options.segmentEndDate] — room segment checkout day (inclusive visual end)
 */
export function resolveCheckedOutBarBounds(booking, checkIn, checkOut, options = {}) {
  const status = String(booking?.status || '').toLowerCase().replace(/\s+/g, '_');
  const isCheckedOut = status === 'checked_out';
  const ci = checkIn && moment.isMoment(checkIn) ? checkIn.clone() : moment(checkIn);
  const co = checkOut && moment.isMoment(checkOut) ? checkOut.clone() : moment(checkOut);
  const isDepartingRoom = options.isDepartingRoom === true;
  const isArrivalRoom = options.isArrivalRoom === true;
  const tz = resolveHotelTimezone(booking);
  const result = {
    checkIn: ci?.isValid?.() ? ci.startOf('day') : ci,
    checkOut: co?.isValid?.() ? co.startOf('day') : co,
    startDayFraction: null,
    endDayFraction: null,
    usedActualStay: false,
  };

  const segmentDateStart = options.segmentStartDate
    ? moment(options.segmentStartDate).startOf('day')
    : null;
  const segmentDateEnd = options.segmentEndDate
    ? moment(options.segmentEndDate).startOf('day')
    : null;
  const segmentStart = options.segmentStartedAt
    ? toHotelPickerMoment(options.segmentStartedAt, tz)
    : null;
  const segmentEnd = options.segmentEndedAt
    ? toHotelPickerMoment(options.segmentEndedAt, tz)
    : null;
  const actualIn = resolveActualCheckInMoment(booking);
  const actualOut = resolveActualCheckOutMoment(booking);
  const expectedIn = resolveExpectedCheckInMoment(booking);
  const expectedOut = resolveExpectedCheckOutMoment(booking);

  const hasSegmentTimes = Boolean(segmentStart?.isValid() || segmentEnd?.isValid());
  const hasSegmentDates = Boolean(segmentDateStart?.isValid() || segmentDateEnd?.isValid());
  const hasClockTimes = Boolean(
    actualIn?.isValid()
    || actualOut?.isValid()
    || expectedIn?.isValid()
    || expectedOut?.isValid(),
  );
  if (!isCheckedOut && !hasSegmentTimes && !hasSegmentDates && !hasClockTimes) {
    return result;
  }

  result.usedActualStay = isCheckedOut || hasSegmentTimes || hasSegmentDates || Boolean(actualIn?.isValid());

  // Departing room + late actual checkout: guest stayed in this room past the booked
  // segment end — allow the bar past scheduled segmentDateEnd (e.g. 1N booked, CO +3 days).
  const actualOutDay = actualOut?.isValid() ? actualOut.clone().startOf('day') : null;
  const allowLateCheckoutPastSegment = Boolean(
    isCheckedOut
    && isDepartingRoom
    && actualOutDay?.isValid()
    && segmentDateEnd?.isValid()
    && actualOutDay.isAfter(segmentDateEnd, 'day'),
  );

  // Align to this physical room's full segment window (room change / upgrade).
  // Nightly rows can be limited to the visible calendar range; in that case their
  // first date is later than the real segment start. Restoring the API segment
  // start makes an off-screen continuation flush to the calendar's left edge.
  if (
    segmentDateStart?.isValid()
    && result.checkIn?.isValid?.()
    && !result.checkIn.isSame(segmentDateStart, 'day')
  ) {
    result.checkIn = segmentDateStart.clone();
  }
  if (
    segmentDateEnd?.isValid()
    && result.checkOut?.isValid?.()
    && result.checkOut.isAfter(segmentDateEnd, 'day')
    && !allowLateCheckoutPastSegment
  ) {
    result.checkOut = segmentDateEnd.clone();
  }

  // --- Start day / fraction ---
  if (segmentStart?.isValid()) {
    const startDay = segmentStart.clone().startOf('day');
    // Align to segment start. Visible booking_items often omit nights before the
    // calendar range — restore the real segment day so bars that began off-screen
    // flush from the calendar left edge (startsBeforeCalendar).
    if (!result.checkIn?.isValid?.() || !result.checkIn.isSame(startDay, 'day')) {
      result.checkIn = startDay.clone();
    }
    if (result.checkIn.isSame(segmentStart, 'day')) {
      result.startDayFraction = dayFractionFromMoment(segmentStart);
    }
  } else if (actualIn?.isValid()) {
    // Arrival room only: align to actual CI day.
    // Pull forward (placeholder before CI) OR back (items truncated at range start).
    // Never do this for later room-change segments (isArrivalRoom=false).
    if (isArrivalRoom && result.checkIn?.isValid?.() && !result.checkIn.isSame(actualIn, 'day')) {
      result.checkIn = actualIn.clone().startOf('day');
    }
    if (result.checkIn?.isValid?.() && result.checkIn.isSame(actualIn, 'day')) {
      result.startDayFraction = dayFractionFromMoment(actualIn);
    }
  } else if (expectedIn?.isValid()) {
    if (isArrivalRoom && result.checkIn?.isValid?.() && result.checkIn.isAfter(expectedIn, 'day')) {
      // Truncated nights before range — restore booked/expected CI day.
      result.checkIn = expectedIn.clone().startOf('day');
    }
    if (result.checkIn?.isValid?.() && result.checkIn.isSame(expectedIn, 'day')) {
      result.startDayFraction = dayFractionFromMoment(expectedIn);
    }
  } else if (isArrivalRoom) {
    const bookedIn = moment(booking?.check_in_date || booking?.start_date).startOf('day');
    if (
      bookedIn.isValid()
      && result.checkIn?.isValid?.()
      && result.checkIn.isAfter(bookedIn, 'day')
    ) {
      result.checkIn = bookedIn.clone();
    }
  }

  // --- End day / fraction ---
  if (
    segmentEnd?.isValid()
    && !(
      isCheckedOut
      && isDepartingRoom
      && actualOut?.isValid()
      && actualOut.isAfter(segmentEnd)
    )
  ) {
    const endDay = segmentEnd.clone().startOf('day');
    result.checkOut = endDay.clone();
    result.endDayFraction = dayFractionFromMoment(segmentEnd);
  } else if (isCheckedOut && actualOut?.isValid()) {
    const outDay = actualOutDay || actualOut.clone().startOf('day');
    if (!result.checkOut?.isValid?.() || result.checkOut.isAfter(outDay, 'day')) {
      // Early checkout: clip.
      result.checkOut = outDay.clone();
    } else if (isDepartingRoom && result.checkOut.isBefore(outDay, 'day')) {
      // Late checkout on the departing room — extend through actual CO day
      // even when booked segment ended earlier (no nightly items for late days).
      result.checkOut = outDay.clone();
    }
    if (result.checkOut?.isValid?.() && result.checkOut.isSame(actualOut, 'day')) {
      result.endDayFraction = dayFractionFromMoment(actualOut);
    }
  } else if (!isCheckedOut && expectedOut?.isValid()) {
    // In-house / confirmed: end at expected checkout (scheduled date + hotel CO policy).
    const outDay = expectedOut.clone().startOf('day');
    if (
      isDepartingRoom
      && result.checkOut?.isValid?.()
      && result.checkOut.isBefore(outDay, 'day')
      && (!segmentDateEnd?.isValid?.() || !outDay.isAfter(segmentDateEnd, 'day'))
    ) {
      result.checkOut = outDay.clone();
    }
    if (result.checkOut?.isValid?.() && result.checkOut.isSame(expectedOut, 'day')) {
      result.endDayFraction = dayFractionFromMoment(expectedOut);
    }
  }

  // Re-clip after temporal adjustments (keep late-checkout departing bar intact).
  if (segmentDateStart?.isValid() && result.checkIn?.isValid?.() && result.checkIn.isBefore(segmentDateStart, 'day')) {
    result.checkIn = segmentDateStart.clone();
  }
  if (
    segmentDateEnd?.isValid()
    && result.checkOut?.isValid?.()
    && result.checkOut.isAfter(segmentDateEnd, 'day')
    && !allowLateCheckoutPastSegment
  ) {
    result.checkOut = segmentDateEnd.clone();
  }

  if (result.checkIn?.isValid?.() && result.checkOut?.isValid?.() && result.checkOut.isBefore(result.checkIn, 'day')) {
    result.checkOut = result.checkIn.clone();
  }

  return result;
}

/** Earliest / latest room segment from API room_segments for arrival/departing flags. */
export function resolveBookingRoomSegmentRoles(roomSegments, roomId) {
  const segments = Array.isArray(roomSegments) ? roomSegments : [];
  const rid = Number(roomId);
  if (!segments.length || !Number.isFinite(rid)) {
    return { isArrivalRoom: true, isDepartingRoom: true, segment: null };
  }
  let earliest = null;
  let latest = null;
  let mine = null;
  segments.forEach((seg) => {
    const segRid = Number(seg?.room_id);
    if (!Number.isFinite(segRid) || !seg?.start_date || !seg?.end_date) return;
    if (segRid === rid) mine = seg;
    if (!earliest || String(seg.start_date) < String(earliest.start_date)) earliest = seg;
    if (!latest || String(seg.end_date) > String(latest.end_date)) latest = seg;
  });
  return {
    isArrivalRoom: Boolean(earliest && Number(earliest.room_id) === rid),
    isDepartingRoom: Boolean(latest && Number(latest.room_id) === rid),
    segment: mine,
  };
}

export function canEditActualCheckIn(booking) {
  if (!booking) return false;
  const status = String(booking.status || '').toLowerCase();
  return Boolean(
    booking.check_in_time
    || ['checked_in', 'partial_checkin', 'partial_checkout', 'checked_out'].includes(status)
  );
}

export function canEditActualCheckOut(booking) {
  if (!booking) return false;
  const status = String(booking.status || '').toLowerCase();
  return Boolean(
    booking.check_out_time
    || ['partial_checkout', 'checked_out'].includes(status)
  );
}
